import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  createRequests,
  fetchCheckPlaybook,
  provideInputs,
} from "../../../../store/remediation/remediationSlice";
import PlaybookInputs from "./PlaybookInputs";
import PlaybookSummary from "./PlaybookSummary";
import ScheduleField from "./ScheduleField";
import { apiErrorSnackbarOpts, friendlySkippedReason } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { useActionFeedback, useIsRemediationAdmin } from "../common/hooks";

/**
 * Request Fix dialog — launched from CSPM when the user selects failing
 * resources in the resource drawer. Never shows internal check_ids in the UI.
 *
 * preset:
 *   checkId, checkTitle, accountId, resourceArns[] (one or many)
 */
const RequestFixForm = ({ open, onClose, preset, onSubmitted }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const isAdmin = useIsRemediationAdmin();
  const notifSettings = useSelector((s) => s.remediation.notificationSettings.data);
  const playbooksMap = useSelector((s) => s.remediation.checkPlaybooks);
  const playbookStatus = useSelector((s) => s.remediation.checkPlaybookStatus);

  const checkId = preset?.checkId || "";
  const checkTitle = preset?.checkTitle || "";
  const resourceArns = useMemo(
    () => (Array.isArray(preset?.resourceArns) ? preset.resourceArns.filter(Boolean) : []),
    [preset?.resourceArns]
  );
  const isMulti = resourceArns.length > 1;

  const [accountNumber, setAccountNumber] = useState(preset?.accountNumber || "");
  const [inputs, setInputs] = useState({});
  const [scheduleMode, setScheduleMode] = useState("asap");
  const [scheduledAt, setScheduledAt] = useState(null);
  const [autoApprove, setAutoApprove] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [inputErrors, setInputErrors] = useState({});

  useEffect(() => {
    if (open) {
      setAccountNumber(preset?.accountNumber || "");
      setInputs({});
      setScheduleMode("asap");
      setScheduledAt(null);
      setAutoApprove(false);
      setScheduleError(null);
      setInputErrors({});
    }
  }, [open, preset]);

  // Load playbook details only when the form opens (not on the CSPM grid).
  useEffect(() => {
    if (open && checkId) {
      dispatch(fetchCheckPlaybook(checkId));
    }
  }, [open, checkId, dispatch]);

  const playbook = checkId ? playbooksMap[checkId] : null;
  const playbookLoading = playbookStatus?.status === "loading" && playbookStatus?.check_id === checkId;

  useEffect(() => {
    if (!playbook?.inputs) return;
    const seeded = {};
    for (const inp of playbook.inputs) {
      if (inp.default !== undefined && inputs[inp.name] === undefined) {
        seeded[inp.name] = inp.default;
      }
    }
    if (Object.keys(seeded).length > 0) {
      setInputs((cur) => ({ ...seeded, ...cur }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbook]);

  const validate = () => {
    let ok = true;
    setScheduleError(null);
    setInputErrors({});

    if (!checkId) {
      showSnackbar({ message: "Check information is missing.", severity: "error" });
      ok = false;
    }
    if (resourceArns.length === 0) {
      showSnackbar({ message: "Select at least one failed resource.", severity: "error" });
      ok = false;
    }
    if (!String(accountNumber || "").trim()) {
      showSnackbar({ message: "Account number is required.", severity: "error" });
      ok = false;
    }

    if (playbook?.inputs) {
      const ie = {};
      for (const inp of playbook.inputs) {
        if (inp.required) {
          const v = inputs[inp.name];
          if (v === undefined || v === null || v === "") {
            ie[inp.name] = "Required.";
            ok = false;
          }
        }
      }
      if (Object.keys(ie).length > 0) setInputErrors(ie);
    }

    if (scheduleMode === "scheduled") {
      if (!scheduledAt) {
        setScheduleError("Pick a date and time.");
        ok = false;
      } else {
        const target = new Date(scheduledAt).getTime();
        const minMins = notifSettings?.pre_execution_notice_minutes ?? 30;
        const minMs = Date.now() + minMins * 60 * 1000;
        const maxMs = Date.now() + 30 * 24 * 60 * 60 * 1000;
        if (target < minMs) {
          setScheduleError(`Time must be at least ${minMins} minutes from now.`);
          ok = false;
        }
        if (target > maxMs) {
          setScheduleError("Time can't be more than 30 days out.");
          ok = false;
        }
      }
    }

    return ok;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const body = {
      account_number: String(accountNumber).trim(),
      items: resourceArns.map((arn) => ({ check_id: checkId, resource_arn: arn })),
      ...(scheduleMode === "scheduled" && scheduledAt ? { scheduled_at: scheduledAt } : {}),
      ...(isAdmin && autoApprove ? { auto_approve: true } : {}),
    };
    setSubmitting(true);
    dispatch(createRequests(body));
  };

  useActionFeedback("create", ({ succeeded = [], skipped = [], error }) => {
    if (!open) return;
    if (error) {
      setSubmitting(false);
      showSnackbar(apiErrorSnackbarOpts(error, "Couldn't file the request."));
      return;
    }
    const newSucceeded = succeeded.filter((s) => !s.deduped_by);
    const isDedup = succeeded.some((s) => s.deduped_by);

    if (newSucceeded.length > 0 && Object.keys(inputs).length > 0) {
      dispatch(
        provideInputs({
          request_ids: newSucceeded.map((s) => s.request_id),
          inputs,
        })
      );
      return;
    }

    setSubmitting(false);
    if (isDedup) {
      showSnackbar({
        message: "A matching request already exists. We've opened that one instead.",
        severity: "info",
      });
    } else if (succeeded.length && skipped.length === 0) {
      showSnackbar({
        message: `Remediation request${resourceArns.length > 1 ? "s" : ""} filed.`,
        severity: "success",
      });
    } else if (succeeded.length && skipped.length) {
      showSnackbar({
        message: "Filed with some skipped items.",
        severity: "warning",
        details: skipped.map((s) => friendlySkippedReason(s)),
      });
    } else {
      showSnackbar({
        message: "Couldn't file the request.",
        severity: "error",
        details: skipped.map((s) => friendlySkippedReason(s)),
      });
    }
    if (typeof onSubmitted === "function") onSubmitted();
    onClose();
  });

  useActionFeedback("inputs", ({ skipped = [], error }) => {
    if (!open) return;
    setSubmitting(false);
    if (error) {
      showSnackbar(apiErrorSnackbarOpts(error, "Couldn't save additional inputs."));
      return;
    }
    if (skipped.length) {
      showSnackbar({
        message: "Filed, but some inputs couldn't be bound.",
        severity: "warning",
        details: skipped.map((s) => friendlySkippedReason(s)),
      });
    } else {
      showSnackbar({ message: "Remediation request filed.", severity: "success" });
    }
    if (typeof onSubmitted === "function") onSubmitted();
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ flexGrow: 1 }}>Request a fix</Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Check</Typography>
        <Typography variant="body1" sx={{ mt: 0.5, mb: 2, fontWeight: 500 }}>
          {checkTitle || "Security check"}
        </Typography>

        <Typography variant="caption" sx={{ fontWeight: 600 }}>Resources</Typography>
        {isMulti ? (
          <Alert severity="info" sx={{ mt: 0.5, mb: 2 }}>
            Requesting remediation for <strong>{resourceArns.length} failed resources</strong> under
            this check. One remediation request will be filed per resource.
          </Alert>
        ) : (
          <Typography variant="body2" sx={{ mt: 0.5, mb: 2, fontFamily: "monospace", fontSize: 13, wordBreak: "break-all" }}>
            {resourceArns[0]}
          </Typography>
        )}

        {playbookLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Loading fix details…</Typography>
          </Box>
        )}

        {playbook && <PlaybookSummary playbook={playbook} />}

        <Typography variant="caption" sx={{ fontWeight: 600 }}>Account</Typography>
        <TextField
          fullWidth
          size="small"
          required
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          label="AWS account number"
          helperText="Pre-filled from the CSPM scan context."
          sx={{ mt: 0.5, mb: 2.5 }}
        />

        {playbook?.inputs?.length > 0 && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>Additional inputs</Typography>
            <PlaybookInputs
              inputs={playbook.inputs}
              values={inputs}
              onChange={setInputs}
              errors={inputErrors}
              accountNumber={accountNumber}
            />
          </>
        )}

        <ScheduleField
          mode={scheduleMode}
          onModeChange={setScheduleMode}
          scheduledAt={scheduledAt}
          onScheduledAtChange={setScheduledAt}
          noticeMinutes={notifSettings?.pre_execution_notice_minutes ?? 30}
          error={scheduleError}
        />

        {isAdmin && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={autoApprove} onChange={(e) => setAutoApprove(e.target.checked)} />
              <Typography variant="body2">Auto-approve</Typography>
              <Tooltip title="Skips the approval step — only available to Administrators.">
                <InfoIcon fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <CircularProgress size={20} color="inherit" /> : "File request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestFixForm;
